import { z } from "zod";
import { normalizeProductionUnit } from "./quantity.js";

export const TERMINAL_WORK_ORDER_SUGGESTION_MIN_LENGTH = 3;
export const TERMINAL_WORK_ORDER_SUGGESTION_LIMIT = 20;

/** Keeps the business identity as text (including leading zeroes) while accepting tablet input without spaces. */
export function normalizeTerminalWorkOrderNumber(value: string) {
  return value.trim().toLocaleUpperCase("tr-TR").replace(/\s+/gu, "");
}

export function sameTerminalWorkOrderNumber(left: string, right: string) {
  return normalizeTerminalWorkOrderNumber(left) === normalizeTerminalWorkOrderNumber(right);
}

export const terminalWorkOrderPlanRevisionSchema = z.object({
  id: z.string().uuid(),
  weekStart: z.string().date(),
  version: z.number().int().positive(),
  refreshedAt: z.string().datetime({ offset: true }),
}).strict();

export const terminalWorkOrderStartEligibilitySchema = z.discriminatedUnion("allowed", [
  z.object({ allowed: z.literal(true), code: z.null(), reason: z.null() }).strict(),
  z.object({
    allowed: z.literal(false),
    code: z.enum(["COUNTER_UNIT_UNVERIFIED", "SOURCE_UNVERIFIED"]),
    reason: z.string().trim().min(1).max(500),
  }).strict(),
]);

export type TerminalWorkOrderStartEligibility = z.infer<typeof terminalWorkOrderStartEligibilitySchema>;
export type TerminalWorkOrderPlanRevision = z.infer<typeof terminalWorkOrderPlanRevisionSchema>;

export function sameTerminalPlanRevision(left: TerminalWorkOrderPlanRevision, right: TerminalWorkOrderPlanRevision | null) {
  return right !== null && left.id === right.id && left.weekStart === right.weekStart && left.version === right.version && left.refreshedAt === right.refreshedAt;
}

export function terminalWorkOrderStartEligibility(
  workOrderUnit: string,
  counterUnit: string | null,
  hasVerifiedCaseConversion: boolean,
): TerminalWorkOrderStartEligibility {
  if (!counterUnit) return { allowed: true, code: null, reason: null };
  const workUnit = normalizeProductionUnit(workOrderUnit);
  const measuredUnit = normalizeProductionUnit(counterUnit);
  if (workUnit && measuredUnit && workUnit === measuredUnit) {
    return { allowed: true, code: null, reason: null };
  }
  if (measuredUnit === "KUTU" && workUnit === "KOLİ" && hasVerifiedCaseConversion) {
    return { allowed: true, code: null, reason: null };
  }
  const reason = measuredUnit === "KUTU" && workUnit === "KOLİ"
    ? "Sayaç kutu, iş emri koli ölçüyor; Mikro stok kartında doğrulanmış kutu/koli dönüşümü bulunamadı."
    : `Sayaç birimi ${measuredUnit || "doğrulanamadı"}, iş emri birimi ${workUnit || "doğrulanamadı"}; bu iş bu hatta başlatılamaz.`;
  return { allowed: false, code: "COUNTER_UNIT_UNVERIFIED", reason };
}

export const terminalOpenWorkOrderSchema = z.object({
  workOrderNo: z.string().trim().min(1).max(80),
  productCode: z.string().trim().min(1).max(80),
  productName: z.string().trim().min(1).max(240),
  plannedQuantity: z.number().finite().nonnegative(),
  unit: z.string().trim().min(1).max(20),
  planNote: z.string().max(2_000).nullable(),
  sourceFingerprint: z.string().regex(/^[0-9a-f]{64}$/u),
  planRevision: terminalWorkOrderPlanRevisionSchema.nullable(),
  startEligibility: terminalWorkOrderStartEligibilitySchema,
}).strict();

export const terminalOpenWorkOrdersResponseSchema = z.object({
  items: z.array(terminalOpenWorkOrderSchema).max(100),
  warning: z.string().max(500).nullable(),
  nextCursor: z.string().trim().min(1).max(2_000).nullable().optional().default(null),
}).strict();

export const terminalDirectWorkOrderNoteAvailabilitySchema = z.enum(["UNSUPPLIED", "NONE", "PRESENT"]);

export const terminalWorkOrderSourceSnapshotSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("DIRECT"),
    noteAvailability: terminalDirectWorkOrderNoteAvailabilitySchema,
    fingerprint: z.string().regex(/^[0-9a-f]{64}$/u),
  }).strict(),
  z.object({
    mode: z.literal("PLANNED"),
    noteAvailability: z.null(),
    fingerprint: z.string().regex(/^[0-9a-f]{64}$/u),
  }).strict(),
]);

export const terminalDirectOpenWorkOrderSchema = z.object({
  workOrderNo: z.string().trim().min(1).max(80),
  productCode: z.string().trim().min(1).max(80),
  productName: z.string().trim().min(1).max(240),
  plannedQuantity: z.number().finite().nonnegative().nullable(),
  unit: z.string().trim().min(1).max(20).nullable(),
  sourceNote: z.string().max(2_000).nullable(),
  sourceNoteAvailability: terminalDirectWorkOrderNoteAvailabilitySchema,
  sourceFingerprint: z.string().regex(/^[0-9a-f]{64}$/u),
  startEligibility: terminalWorkOrderStartEligibilitySchema,
}).strict().superRefine((item, context) => {
  const hasNote = typeof item.sourceNote === "string" && item.sourceNote.trim().length > 0;
  const noteMatchesAvailability = item.sourceNoteAvailability === "PRESENT"
    ? hasNote
    : item.sourceNote === null;
  if (!noteMatchesAvailability) {
    context.addIssue({
      code: "custom",
      path: ["sourceNote"],
      message: "Kaynak notu ile not mevcudiyeti birbiriyle tutarlı olmalıdır.",
    });
  }
});

export const terminalDirectOpenWorkOrdersResponseSchema = z.object({
  items: z.array(terminalDirectOpenWorkOrderSchema).max(100),
  warning: z.string().max(500).nullable(),
  nextCursor: z.null(),
}).strict();

export const terminalWeeklyPlanTotalSchema = z.object({
  quantity: z.number().finite().nonnegative().nullable(),
  unit: z.enum(["kg", "koli", "adet"]).nullable(),
  country: z.string().trim().min(1).max(120).nullable(),
  revision: terminalWorkOrderPlanRevisionSchema,
}).strict();
export type TerminalWeeklyPlanTotal = z.infer<typeof terminalWeeklyPlanTotalSchema>;

export const terminalPlanWorkOrderSuggestionSchema = z.object({
  workOrderNo: z.string().trim().min(1).max(80),
  productCode: z.string().trim().min(1).max(80),
  productName: z.string().trim().min(1).max(240),
  plannedQuantity: z.number().finite().nonnegative(),
  planNote: z.string().max(2_000).nullable(),
  completed: z.boolean().default(false),
  weeklyPlan: terminalWeeklyPlanTotalSchema.nullable().optional(),
}).strict();

export const terminalPlanWorkOrderSuggestionsResponseSchema = z.object({
  items: z.array(terminalPlanWorkOrderSuggestionSchema).max(TERMINAL_WORK_ORDER_SUGGESTION_LIMIT),
  warning: z.string().max(500).nullable(),
  nextCursor: z.string().trim().min(1).max(2_000).nullable(),
}).strict();

export const terminalWorkOrderStartInputSchema = z.object({
  workOrderNo: z.string().trim().min(1).max(80),
  productCode: z.string().trim().min(1).max(80),
  sourceMode: z.enum(["PLANNED", "DIRECT"]).optional(),
  expectedSourceFingerprint: z.string().regex(/^[0-9a-f]{64}$/u),
  expectedPlanRevision: terminalWorkOrderPlanRevisionSchema.nullable().optional(),
  planNoteAcknowledged: z.boolean(),
}).strict();

export type TerminalOpenWorkOrder = z.infer<typeof terminalOpenWorkOrderSchema>;
export type TerminalDirectOpenWorkOrder = z.infer<typeof terminalDirectOpenWorkOrderSchema>;
export type TerminalDirectOpenWorkOrdersResponse = z.infer<typeof terminalDirectOpenWorkOrdersResponseSchema>;
export type TerminalDirectWorkOrderNoteAvailability = z.infer<typeof terminalDirectWorkOrderNoteAvailabilitySchema>;
export type TerminalWorkOrderSourceSnapshot = z.infer<typeof terminalWorkOrderSourceSnapshotSchema>;
export type TerminalPlanWorkOrderSuggestion = z.infer<typeof terminalPlanWorkOrderSuggestionSchema>;
export type TerminalWorkOrderStartInput = z.infer<typeof terminalWorkOrderStartInputSchema>;
